/**
 * Threedium Solution Picker Component
 *
 * A searchable, visual gallery for browsing and selecting Threedium
 * solutions (3D experiences) when creating new projects.
 *
 * The panel renders via a React portal so it is never clipped by
 * ancestor overflow:hidden containers (e.g. builder-start-screen).
 * It opens as a flyout beside the trigger (right, then left), falling
 * back to a centered overlay when neither side has room, so its size
 * is independent of how close the trigger sits to the viewport edge.
 */
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { threediumApi } from '../services/cloudApi';
import { formatErrorMessage } from '../utils/errorUtils';
import './ThreediumSolutionPicker.css';

// Flyout panel sizing (px) — used when the picker is not inside a
// recognizable "stage" container (see PANEL_STAGE_SELECTOR below).
const PANEL_WIDTH = 460;
const PANEL_MAX_HEIGHT = 620;
const PANEL_GAP = 12;        // distance between trigger and panel
const VIEWPORT_MARGIN = 16;  // minimum distance from viewport edges

// When the picker lives inside this container (the start-screen "stage"),
// the panel is sized to a fraction of that box and centered over it, so it
// reads as a gallery within the content area rather than a small dropdown.
const PANEL_STAGE_SELECTOR = '.builder-start-screen';
const PANEL_SIZE_RATIO = 0.8;   // ~80% of the stage, per design request
const PANEL_MIN_WIDTH = 380;    // keep it usable on small stages
const PANEL_MIN_HEIGHT = 380;

const AlertIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
);

const CubeIcon = () => (
    <svg width="28" height="28" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round">
        <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z"/>
        <path d="M8 15V8"/>
        <path d="M2 4.5L8 8L14 4.5"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 7L5.5 10L11.5 4"/>
    </svg>
);

const XIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

export function ThreediumSolutionPicker({
    value,
    onChange,
    placeholder = 'Search for a solution...',
    disabled = false
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [solutions, setSolutions] = useState([]);
    const [selectedSolution, setSelectedSolution] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasKeys, setHasKeys] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [dropdownPos, setDropdownPos] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');

    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const requestIdRef = useRef(0);
    const initialLoadDoneRef = useRef(false);

    // ── API key check (once on mount) ──────────────────────────
    useEffect(() => {
        const checkKeys = async () => {
            try {
                const status = await threediumApi.getKeysStatus();
                setHasKeys(status.has_keys && status.is_valid);
            } catch (err) {
                console.error('Failed to check Threedium keys:', err);
                setHasKeys(false);
            }
        };
        checkKeys();
    }, []);

    useEffect(() => {
        if (!hasKeys) return;
        threediumApi.listProjects({ limit: 100 })
            .then((result) => setProjects(result.projects || []))
            .catch((err) => console.error('Failed to load Threedium projects:', err));
    }, [hasKeys]);

    // ── Staleness-aware search ─────────────────────────────────
    const searchSolutions = useCallback(async (query, pageNum = 1, append = false) => {
        if (!hasKeys) return;

        const thisRequest = ++requestIdRef.current;

        setIsLoading(true);
        setError(null);

        try {
            const result = selectedProjectId
                ? await threediumApi.listProjectSolutions(selectedProjectId, {
                    search: query || undefined,
                    limit: 20,
                    page: pageNum
                })
                : await threediumApi.listSolutions({
                    search: query || undefined,
                    limit: 20,
                    page: pageNum
                });

            if (requestIdRef.current !== thisRequest) return;

            if (append) {
                setSolutions(prev => [...prev, ...result.solutions]);
            } else {
                setSolutions(result.solutions);
            }

            setHasMore(result.has_more);
            setPage(pageNum);
        } catch (err) {
            if (requestIdRef.current !== thisRequest) return;
            console.error('Failed to search solutions:', err);
            setError(err.message || 'Failed to load solutions');
            if (!append) setSolutions([]);
        } finally {
            if (requestIdRef.current === thisRequest) {
                setIsLoading(false);
            }
        }
    }, [hasKeys, selectedProjectId]);

    // ── Reset load-tracking when the dropdown closes ───────────
    useEffect(() => {
        if (!isOpen) {
            initialLoadDoneRef.current = false;
        }
    }, [isOpen]);

    // ── Unified search effect ──────────────────────────────────
    // First render after open → immediate full-list load.
    // Subsequent searchQuery changes → 300 ms debounce.
    useEffect(() => {
        if (!isOpen || !hasKeys) return;

        if (!initialLoadDoneRef.current) {
            initialLoadDoneRef.current = true;
            searchSolutions('');
            return;
        }

        const timer = setTimeout(() => {
            searchSolutions(searchQuery, 1, false);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, isOpen, hasKeys, searchSolutions, selectedProjectId]);

    // ── Portal positioning ─────────────────────────────────────
    // Prefer a flyout to the right of the trigger, then the left.
    // When neither side fits (narrow viewports), center the panel
    // over the viewport like a dialog. Height is clamped to the
    // viewport rather than to the space below the trigger, so the
    // list is always tall enough to browse comfortably.
    const updateDropdownPos = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Stage-relative sizing: when the picker sits inside the start-screen
        // stage, make the panel ~80% of that box and center it over the stage
        // (clamped to the viewport). The responsive grid handles the extra
        // width by adding columns, so wider panels stay visually clean.
        const stage = triggerRef.current.closest(PANEL_STAGE_SELECTOR);
        if (stage) {
            const s = stage.getBoundingClientRect();
            let width = Math.max(s.width * PANEL_SIZE_RATIO, PANEL_MIN_WIDTH);
            width = Math.round(Math.min(width, vw - VIEWPORT_MARGIN * 2));
            let maxHeight = Math.max(s.height * PANEL_SIZE_RATIO, PANEL_MIN_HEIGHT);
            maxHeight = Math.round(Math.min(maxHeight, vh - VIEWPORT_MARGIN * 2));

            const left = Math.round(Math.max(
                VIEWPORT_MARGIN,
                Math.min(s.left + (s.width - width) / 2, vw - width - VIEWPORT_MARGIN)
            ));
            const top = Math.round(Math.max(
                VIEWPORT_MARGIN,
                Math.min(s.top + (s.height - maxHeight) / 2, vh - maxHeight - VIEWPORT_MARGIN)
            ));

            setDropdownPos({ top, left, width, maxHeight, placement: 'center' });
            return;
        }

        const width = Math.min(PANEL_WIDTH, vw - VIEWPORT_MARGIN * 2);
        const maxHeight = Math.min(PANEL_MAX_HEIGHT, vh - VIEWPORT_MARGIN * 2);

        const fitsRight = rect.right + PANEL_GAP + width + VIEWPORT_MARGIN <= vw;
        const fitsLeft = rect.left - PANEL_GAP - width >= VIEWPORT_MARGIN;

        let placement;
        let left;
        if (fitsRight) {
            placement = 'right';
            left = rect.right + PANEL_GAP;
        } else if (fitsLeft) {
            placement = 'left';
            left = rect.left - PANEL_GAP - width;
        } else {
            placement = 'center';
            left = Math.max(
                VIEWPORT_MARGIN,
                Math.min(rect.left + rect.width / 2 - width / 2, vw - width - VIEWPORT_MARGIN)
            );
        }

        // Align the panel top with the trigger, clamped so the whole
        // panel stays on screen even when the trigger sits near the
        // bottom of the viewport.
        const top = Math.max(
            VIEWPORT_MARGIN,
            Math.min(rect.top, vh - maxHeight - VIEWPORT_MARGIN)
        );

        setDropdownPos({ top, left, width, maxHeight, placement });
    }, []);

    useLayoutEffect(() => {
        if (isOpen) updateDropdownPos();
    }, [isOpen, updateDropdownPos]);

    useEffect(() => {
        if (!isOpen) return;
        const reposition = () => updateDropdownPos();
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [isOpen, updateDropdownPos]);

    // ── Click-outside (accounts for portal) ────────────────────
    useEffect(() => {
        const handleClickOutside = (event) => {
            const inWrapper = wrapperRef.current?.contains(event.target);
            const inDropdown = dropdownRef.current?.contains(event.target);
            if (!inWrapper && !inDropdown) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Escape closes the panel ────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    // ── Resolve pre-selected value ─────────────────────────────
    useEffect(() => {
        if (value && !selectedSolution && hasKeys) {
            const existing = solutions.find(s => String(s.id) === String(value));
            if (existing) {
                setSelectedSolution(existing);
            } else {
                threediumApi.getSolution(value)
                    .then(result => {
                        if (result.solution) setSelectedSolution(result.solution);
                    })
                    .catch(err => console.error('Failed to load solution:', err));
            }
        }
    }, [value, selectedSolution, solutions, hasKeys]);

    // ── Handlers ───────────────────────────────────────────────
    const handleSelect = (solution) => {
        setSelectedSolution(solution);
        onChange(String(solution.id));
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedSolution(null);
        onChange('');
    };

    const handleLoadMore = () => {
        if (!isLoading && hasMore) {
            searchSolutions(searchQuery, page + 1, true);
        }
    };

    // ── No-keys fallback ───────────────────────────────────────
    if (!hasKeys) {
        return (
            <div className="solution-picker no-keys">
                <label>Solution ID (Manual)</label>
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Enter Threedium Solution ID (e.g., 55757)"
                    disabled={disabled}
                    className="manual-input"
                />
                <p className="hint">
                    Sign in with Threedium so your solutions can be listed here,
                    or enter a solution ID manually.
                </p>
            </div>
        );
    }

    // ── Portal flyout panel content ────────────────────────────
    const dropdownContent = isOpen && dropdownPos && (
        <div className="solution-picker">
            <div
                ref={dropdownRef}
                className={`picker-dropdown picker-dropdown-portal picker-panel placement-${dropdownPos.placement}`}
                style={{
                    position: 'fixed',
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                    maxHeight: dropdownPos.maxHeight,
                }}
                role="dialog"
                aria-label="Select a Threedium solution"
            >
                <div className="picker-panel-header">
                    <span className="picker-panel-title">Select a solution</span>
                    <button
                        className="picker-panel-close"
                        onClick={() => setIsOpen(false)}
                        aria-label="Close"
                    >
                        <XIcon size={14} />
                    </button>
                </div>

                <div className="search-box">
                    <div className="search-input-row">
                        <svg className="picker-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="7" cy="7" r="4"/>
                            <path d="M10 10L13 13" strokeLinecap="round"/>
                        </svg>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name or ID..."
                            autoFocus
                        />
                        {searchQuery && (
                            <button
                                className="clear-search"
                                onClick={() => setSearchQuery('')}
                            >
                                <XIcon />
                            </button>
                        )}
                    </div>
                    {projects.length > 0 && (
                        <select
                            className="project-filter"
                            value={selectedProjectId}
                            onChange={(e) => {
                                setSelectedProjectId(e.target.value);
                                setPage(1);
                            }}
                        >
                            <option value="">All projects</option>
                            {projects.map((project) => (
                                <option key={project.id} value={project.id}>
                                    {project.title || `Project ${project.id}`}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="solutions-list solutions-grid">
                    {error ? (
                        <div className="error-state">
                            <span><AlertIcon /> {formatErrorMessage(error)}</span>
                            <button onClick={() => searchSolutions(searchQuery)}>
                                Retry
                            </button>
                        </div>
                    ) : isLoading && solutions.length === 0 ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <span>Loading solutions...</span>
                        </div>
                    ) : solutions.length === 0 ? (
                        <div className="empty-state">
                            {searchQuery
                                ? `No solutions found for "${searchQuery}"`
                                : 'No solutions available'
                            }
                        </div>
                    ) : (
                        <>
                            {solutions.map((solution) => {
                                const isSelected = selectedSolution?.id === solution.id;
                                return (
                                    <div
                                        key={solution.id}
                                        className={`solution-card ${isSelected ? 'selected' : ''}`}
                                        onClick={() => handleSelect(solution)}
                                        title={solution.description || solution.title}
                                    >
                                        <div className="solution-card-thumb">
                                            {solution.thumbnail_url ? (
                                                <img
                                                    src={solution.thumbnail_url}
                                                    alt=""
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="solution-card-thumb-placeholder">
                                                    <CubeIcon />
                                                </div>
                                            )}
                                            {isSelected && (
                                                <span className="solution-card-check">
                                                    <CheckIcon />
                                                </span>
                                            )}
                                        </div>
                                        <div className="solution-card-body">
                                            <span className="solution-card-title">
                                                {solution.title}
                                            </span>
                                            <span className="solution-card-meta">
                                                ID: {solution.id}
                                                {solution.project_title && ` • ${solution.project_title}`}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}

                            {hasMore && (
                                <button
                                    className="load-more-btn"
                                    onClick={handleLoadMore}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Loading...' : 'Load More'}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    // ── Render ─────────────────────────────────────────────────
    return (
        <div className="solution-picker" ref={wrapperRef}>
            <label>Threedium Solution</label>

            <div
                ref={triggerRef}
                className={`picker-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {selectedSolution ? (
                    <div className="selected-solution">
                        {selectedSolution.thumbnail_url && (
                            <img
                                src={selectedSolution.thumbnail_url}
                                alt=""
                                className="selected-thumb"
                            />
                        )}
                        <div className="solution-info">
                            <span className="solution-title">{selectedSolution.title}</span>
                            <span className="solution-id">ID: {selectedSolution.id}</span>
                        </div>
                        <button
                            className="clear-btn"
                            onClick={handleClear}
                            title="Clear selection"
                        >
                            <XIcon />
                        </button>
                    </div>
                ) : (
                    <span className="placeholder">{placeholder}</span>
                )}
                <span className="chevron">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {isOpen ? (
                            <polyline points="18 15 12 9 6 15"/>
                        ) : (
                            <polyline points="6 9 12 15 18 9"/>
                        )}
                    </svg>
                </span>
            </div>

            {dropdownContent && createPortal(dropdownContent, document.body)}
        </div>
    );
}

export default ThreediumSolutionPicker;
